import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RecentMessages = ({ messages, onViewAll, className = '' }) => {
  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const messageTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - messageTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const truncateMessage = (message, maxLength = 60) => {
    if (message?.length <= maxLength) return message;
    return message?.substring(0, maxLength) + '...';
  };

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="MessageCircle" size={18} className="text-primary" />
          <h2 className="font-semibold text-foreground">Recent Messages</h2>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onViewAll}
          iconName="ExternalLink"
          iconSize={14}
        >
          View All
        </Button>
      </div>
      {/* Messages List */}
      <div className="divide-y divide-border">
        {messages?.length > 0 ? (
          messages?.map((message) => (
            <div
              key={message?.id}
              className={`p-4 transition-colors ${
                !message?.read ? 'bg-accent/5' : ''
              }`}
            >
              <div className="flex items-start space-x-3">
                {/* Customer Avatar */}
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-primary-foreground">
                      {message?.customerName?.charAt(0)}
                    </span>
                  </div>
                </div>

                {/* Message Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className={`text-sm font-medium ${
                      !message?.read ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      {message?.customerName}
                    </h4>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-muted-foreground">
                        {formatTimestamp(message?.timestamp)}
                      </span>
                      {!message?.read && (
                        <div className="w-2 h-2 bg-accent rounded-full"></div>
                      )}
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-1">
                    {message?.enquiryDestination}
                  </p>
                  
                  <p className={`text-sm ${
                    !message?.read ? 'text-foreground' : 'text-muted-foreground'
                  }`}>
                    {message?.isFromCustomer ? '' : 'You: '}
                    {truncateMessage(message?.content)}
                  </p>

                  {/* Message Type Indicators */}
                  <div className="flex items-center space-x-2 mt-2">
                    {message?.hasAttachment && (
                      <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                        <Icon name="Paperclip" size={12} />
                        <span>Attachment</span>
                      </div>
                    )}
                    
                    {message?.messageType === 'proposal' && (
                      <div className="flex items-center space-x-1 text-xs text-accent">
                        <Icon name="FileText" size={12} />
                        <span>Proposal</span>
                      </div>
                    )}
                    
                    {message?.messageType === 'booking_request' && (
                      <div className="flex items-center space-x-1 text-xs text-success">
                        <Icon name="Calendar" size={12} />
                        <span>Booking Request</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="p-8 text-center">
            <Icon name="MessageCircle" size={32} className="mx-auto mb-3 text-muted-foreground opacity-50" />
            <p className="text-sm text-muted-foreground mb-1">No recent messages</p>
            <p className="text-xs text-muted-foreground">
              Messages from customers will appear here
            </p>
          </div>
        )}
      </div>
      {/* Quick Actions Footer */}
      {messages?.length > 0 && (
        <div className="p-3 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {messages?.filter(m => !m?.read)?.length} unread messages
            </span>
            <button
              onClick={onViewAll}
              className="text-primary hover:text-primary/80 font-medium"
            >
              View all conversations →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecentMessages;